import React, { useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { Button } from '@/components/buttons/Button';
import Divider from './Divider';
import PromptMessage from '@/components/UI/PromptMessage';
import Form from '@/components/form/Form';
import SwitchField from '@/components/form/switch/SwitchField';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { selectBtag, selectLocale } from '@/store/selectors/commonSelectors';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { useRegistrationActions, useRegistrationData } from '@/store/zustand/registration-store';
import BffApiService from '@/shared/bffApiService';
import { runVerify } from '@/store/actions/kycActions';
import { loginSuccess } from '@/store/actions/auth';
import { logException } from '@/utils/logger';
import { triggerNetreferSignup } from '@/shared/netrefer';
import { selectRegistrationOpenedFrom } from '@/store/ui/selectors';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import {
  BffErrorCode,
  BffRegisterAgcoRequestV2,
  BffRegisterResponse,
} from '@lucky7ventures/bff-types';
import { BffApiError } from '@/models/bff-api-error';
import StorageManager, { ReturningUserLoginMethod } from '@/shared/storageManager';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';
import { seonService } from '@/shared/seonService';

type ConsentsFormFields = {
  tcConsent: boolean;
  marketingConsent: boolean;
  pepHioConsent: boolean;
  proxyConsent: boolean;
};

const ConsentsForm = ({
  stepBack,
  setShowError,
  closeModal,
}: {
  stepBack: VoidFunction;
  setShowError: (errorCode: string | null) => void;
  closeModal: VoidFunction;
}) => {
  const registrationData = useRegistrationData();
  const { reset: resetRegistrationStore } = useRegistrationActions();
  const { request: registerRequest, loading: registerLoading } =
    useBffApiRequest<BffRegisterResponse>();
  const [errorMessage, setErrorMessage] = useState('');
  const btag = useSelector(selectBtag);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useAbsoluteNavigate();
  const locale = useSelector(selectLocale);
  const registrationOpenedFrom = useSelector(selectRegistrationOpenedFrom);
  const { pagePath: rewardsWheelPagePath } = useRewardsWheel();

  const data = [
    {
      name: 'marketingConsent',
      label: intl.formatMessage({ id: 'inputs.marketing' }),
      labelClassName: 'text-white font-bold',
    },
    {
      name: 'tcConsent',
      label: (
        <FormattedMessage
          id="inputs.tc"
          values={{
            a: str => (
              <a
                href={`${window.location.origin}${
                  locale === 'ca-fr' ? '/fr' : ''
                }/terms-and-conditions`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-light font-bold underline cursor-pointer relative z-[100]"
              >
                {str}
              </a>
            ),
            b: str => (
              <a
                href={`${window.location.origin}${locale === 'ca-fr' ? '/fr' : ''}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-light font-bold underline cursor-pointer"
              >
                {str}
              </a>
            ),
          }}
        />
      ),
      labelClassName: 'text-white',
      validationErrorEvent: GTagEvents.ont_reg_terms_error,
    },
    {
      name: 'pepHioConsent',
      label: intl.formatMessage({ id: 'inputs.pepHio' }),
      labelClassName: 'text-white',
      validationErrorEvent: GTagEvents.ont_reg_pep_error,
    },
    {
      name: 'proxyConsent',
      label: intl.formatMessage({ id: 'inputs.proxy' }),
      labelClassName: 'text-white',
      validationErrorEvent: GTagEvents.ont_reg_3rdparty_error,
    },
  ];

  const initialValues: ConsentsFormFields = {
    tcConsent: true,
    marketingConsent: false,
    pepHioConsent: true,
    proxyConsent: true,
  };

  const validationSchema = Yup.object().shape({
    tcConsent: Yup.bool()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .test('tc', intl.formatMessage({ id: 'inputs.tc.invalid' }), value => value!),
    marketingConsent: Yup.bool().required(intl.formatMessage({ id: 'input.error.required' })),
    pepHioConsent: Yup.bool()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .test('pepHio', intl.formatMessage({ id: 'inputs.pepHio.invalid' }), value => value!),
    proxyConsent: Yup.bool()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .test('proxy', intl.formatMessage({ id: 'inputs.proxy.invalid' }), value => value!),
  });

  const handleOnSubmit = (values: ConsentsFormFields): void => {
    const { birthDate, ...payloadData } = registrationData;
    const { day, month, year } = birthDate;
    const formattedDateOfBirth = `${year}-${month}-${day}`;
    triggerGTag(GTagEvents.ont_reg_step4_submit_cta_click);

    const payload: BffRegisterAgcoRequestV2 = {
      email: payloadData.email,
      password: payloadData.password,
      firstName: payloadData.firstName,
      lastName: payloadData.lastName,
      birthDate: formattedDateOfBirth,
      address: payloadData.address,
      houseNumber: payloadData.houseNumber,
      postalCode: payloadData.postCode,
      city: payloadData.city,
      mobilePrefix: payloadData.mobile.prefix,
      mobile: payloadData.mobile.number,
      occupationId: Number(payloadData.occupationId),
      industryId: Number(payloadData.industryId),
      citizenshipId: payloadData.citizenshipId,
      emailConsented: values.marketingConsent,
      smsConsented: values.marketingConsent,
      provinceCode: payloadData.provinceCode,
      btag,
    };

    const handleOnSuccess = ({ userId, token }: BffRegisterResponse) => {
      StorageManager.setIsReturningUser(ReturningUserLoginMethod.Email);
      triggerGTag(GTagEvents.ont_reg_success, { userId }, () => {
        triggerNetreferSignup(btag, userId, locale);
      });
      sendEventToAppWrapper(GTagEvents.ont_reg_success, {
        userId,
      });

      dispatch(loginSuccess(token!, { isRegistration: true }));
      resetRegistrationStore();
      seonService.checkFraudEvent('register');
      // Starts the GBG Verification process in the background
      dispatch(runVerify());
      closeModal();

      // do specific redirects for some cases if registration was started on specific place
      if (registrationOpenedFrom === OpenedFrom.RewardsWheel) {
        navigate(`/${rewardsWheelPagePath}`);
      } else {
        navigate('/');
      }
    };

    const handleOnError = (error: BffApiError) => {
      logException(new Error('Ontario Reg Failed'), { error });

      const errorMessage = generateBffErrorMessageWithCode(
        intl.formatMessage({
          id:
            error.code === BffErrorCode.REGISTAR_SELF_EXCLUSION
              ? 'registration.error.centralSelfExclusion'
              : 'failedSignup.SomethingWentWrongMessage',
        }),
        error.code,
      );

      triggerGTag(GTagEvents.ont_reg_failed, { error: `bff-error-${error.code}` });
      // 1. for error ACCOUNT_BLOCKED we want to do a special error handling, also we want to clear the registration state in this case
      if (error.code === BffErrorCode.GIG_ACCOUNT_BLOCKED) {
        resetRegistrationStore();
        setShowError(errorMessage);
        return;
      }
      setErrorMessage(errorMessage);
    };

    setShowError(null);
    setErrorMessage('');
    registerRequest({
      apiMethod: BffApiService.registerAgco,
      payload,
      successCallback: handleOnSuccess,
      errorCallback: handleOnError,
    });
  };

  return (
    <div className="w-full h-full pt-4">
      <h4 className="text-blue-light m-0 font-normal mb-8">
        {intl.formatMessage({ id: 'registration.ontario.consentsTitle' })}
      </h4>
      <h5 className="text-white m-0 mb-5 mt-1.5 text-sm sm:text-base">
        {intl.formatMessage({ id: 'registration.ontario.consentsSubtitle' })}
      </h5>
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        className="flex flex-col justify-between gap-8"
      >
        {() => (
          <>
            {data.map((consent, index, arr) => (
              <React.Fragment key={consent.name}>
                <SwitchField
                  name={consent.name}
                  label={consent.label}
                  labelClassName={consent.labelClassName}
                  onValidationError={error => {
                    if (consent.validationErrorEvent) {
                      triggerGTag(consent.validationErrorEvent, { error });
                    }
                  }}
                />
                {index < arr.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            <div className="flex flex-col gap-4 [&>div:first-child]:m-0">
              {errorMessage && <PromptMessage message={errorMessage} type="error" />}
              <div className="flex flex-col-reverse items-center gap-4 mt-4 sm:flex-row">
                <Button
                  text={intl.formatMessage({ id: 'misc.back' })}
                  onClickHandler={() => {
                    triggerGTag(GTagEvents.ont_reg_step4_back_cta_click);
                    stepBack();
                  }}
                  btnType="secondary"
                />
                <Button
                  text={intl.formatMessage({ id: 'registration.ontario.finish' })}
                  type="submit"
                  btnType="primary"
                  isLoading={registerLoading}
                />
              </div>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default ConsentsForm;
